import { useEffect, useState, useCallback, useMemo, useRef, useContext } from "react";
import { Box,Button,Container,Typography} from "@mui/material";
import { DataGrid,GridToolbarColumnsButton,GridToolbarExportContainer,GridToolbarQuickFilter} from "@mui/x-data-grid";
import { SystemUpdateAltOutlined } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import { callPostApi } from "../../function/ApiCallFunction";
import GradientLoader from "../../components/CommanComponents/GradientLoader";
import CustomFooterWithLoadMore from "../../include/footerWithLoadMore";
import { DataContext } from "../../context/DataContext";

const LotRedemmCustList = () => 
{
    const navigate = useNavigate();
    const {loginData }  = useContext(DataContext);
    const store_id      = loginData?.store_info?.store_id; 

  /* ===================== STATES ===================== */
  const [allData,        setAllData]        = useState([]);
  const [loading,        setLoading]        = useState(false);
  const [pageIndex,      setPageIndex]      = useState(0);
  const [hasMoreData,    setHasMoreData]    = useState(true);
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [filtersLoaded,  setFiltersLoaded]  = useState(false);

  const isInitialSessionLoad = useRef(false);
  const hasFetchedRef        = useRef(false);

  /* ===================== FILTERS ===================== */
  const defaultFilters = useMemo(() => ({store_id, genLotNumber: "",loadingNumber: "",location: "",status: "Redeemed",fetchLimit: '10', pageSize: '10'}),[store_id]);
  const [filters, setFilters] = useState(defaultFilters);

  /* ===================== RESET ===================== */
  const resetDataStates = () => 
  {
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      setLoading(false);
  };

  /* ===================== APPLY FILTER ===================== */
  const applyFilters = () => 
  {
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      setFiltersApplied(true);
  };

  const handleEnterApplyFilters = (e) => { if (e.key === 'Enter') applyFilters(); };

  /* ===================== CLEAR FILTER ===================== */
  const clearFilters = () => 
  {
      setFilters(defaultFilters);
      setAllData([]);
      setPageIndex(0);
      setHasMoreData(true);
      sessionStorage.setItem('redeemedFilter', JSON.stringify(defaultFilters));
      setFiltersApplied(true);
  };

  /* ===================== LOAD FROM SESSION ===================== */
  useEffect(() => 
  {
    const storedState = sessionStorage.getItem("redeemedFilter");
    if (storedState) 
    {
        try 
        {
            const parsedState = JSON.parse(storedState);
            setFilters({ ...defaultFilters, ...parsedState });
            isInitialSessionLoad.current = true;
            setFiltersApplied(true);
        } 
        catch { sessionStorage.removeItem("redeemedFilter"); }
    }
  }, [defaultFilters]);

  /* ===================== FETCH API ===================== */
  const fetchData = useCallback(async (page = pageIndex) => 
  {
      if (!filtersLoaded || loading || !hasMoreData) return;

      setLoading(true);
      try 
      {
          const StartNum = page * Number(filters.fetchLimit);
          const payload = {mod: "CASHI_QECODE_LIST",data_arr: {...filters,qrcodeStatus:'2',start: StartNum,limit: filters.fetchLimit,}};

          const response = await callPostApi("cashi-qrcode", payload);

          if (response.status === "200" && response.data) 
          {
              const apiResponse = response.data;

              if (apiResponse.length > 0) 
              {
                setAllData(prev => [...prev, ...apiResponse]);
                setPageIndex(page);
              } 
              else {  setHasMoreData(false); }

              if (apiResponse.length < filters.fetchLimit) { setHasMoreData(false); }

          } else { setHasMoreData(false); }
      } 
      catch { setHasMoreData(false); } 
      finally { setLoading(false); }

    },[filters, filtersLoaded, loading, hasMoreData, pageIndex]);

  /* ===================== INITIAL LOAD ===================== */  
  // Initial fetch trigger
  useEffect(() => {
      if (!hasFetchedRef.current && filtersLoaded) {
          hasFetchedRef.current = true;
          fetchData();
      }
  }, [filtersLoaded, fetchData]);

  // Save to sessionStorage
  useEffect(() => {
      if (filtersApplied) 
      {
          sessionStorage.setItem('redeemedFilter', JSON.stringify(filters));
          if (!isInitialSessionLoad.current) { fetchData(0); }
          isInitialSessionLoad.current = false;
          setFiltersApplied(false);
      }
  }, [filtersApplied, filters, fetchData]);

  // Set filters loaded once
  useEffect(() => { if (!filtersLoaded) setFiltersLoaded(true); }, [filtersLoaded]);

  /* ===================== TOOLBAR ===================== */
  const CustomToolbar = () => (
    <GridToolbarExportContainer sx={{ justifyContent: "space-between", px: 2 }}>
      <GridToolbarColumnsButton />
      <GridToolbarQuickFilter debounceMs={300} />
    </GridToolbarExportContainer>
  );

  const fetchLimits    = [ "5", "10", "20", "50", "100", "250", "500", "1000"];

  /* ===================== UI ===================== */
  return (
    <Container sx={{ my: 2 }}>

        {/* Buttons */}
        <Box sx={{ display: "flex", mb: 2 }}>
            <Button variant="contained" onClick={() => navigate(-1)}>Back</Button>
            <Button sx={{ ml: "auto", background: "#000", color: "#fff" }} onClick={clearFilters}>Clear Filters</Button>
        </Box>

      {/* Title */}
      <Typography variant="h6" sx={{background: "linear-gradient(195deg, #49a3f1, #1A73E8)",p: 1,color: "#fff",
          borderRadius: 1,mb: 2,display: "flex",alignItems: "center"}}>
          <SystemUpdateAltOutlined sx={{ mr: 1 }} /> Redeemed QR List
      </Typography>

      {/* Filters */}
      <div className='mb-3'>
          <div className="row align-items-center g-2 searchFilter">
              
              <div className="col-12 col-md-2">
                  <input type="text" className="form-control" placeholder="Lot Number" value={filters.genLotNumber}
                      onChange={(e) => setFilters(prev => ({ ...prev, genLotNumber: e.target.value }))} onKeyDown={handleEnterApplyFilters} />
              </div>

              <div className="col-12 col-md-2">
                  <input type="text" className="form-control" placeholder="Loading Number" value={filters.loadingNumber}
                      onChange={(e) => setFilters(prev => ({ ...prev, loadingNumber: e.target.value }))} onKeyDown={handleEnterApplyFilters} />
              </div>

              {/* Page Size Filter */}  
              <div className="col-12 col-md-2">
                  <select className="form-control" value={filters.fetchLimit} onChange={(e) => setFilters(prev => ({ ...prev, fetchLimit: e.target.value }))} >
                      <option value="">Record Fetch Limit</option>
                      {fetchLimits.map(value => ( <option key={value} value={value}>{value}</option>))}
                  </select>
              </div>  

              {/* Apply flter */}
              <div className="col-12 col-md-2 ms-auto">                                        
                  <Button  sx={{background:'linear-gradient(195deg, #49a3f1, #1A73E8)', color:'#ffffff',ml:1,
                      boxShadow: 'rgba(0, 0, 0, 0.02) 0px 1px 3px 0px, rgba(27, 31, 35, 0.15) 0px 0px 0px 1px' }}
                          style={{float: 'right'}}  onClick={() => { resetDataStates(); applyFilters(); }} >Apply Filters</Button>
              </div>  
              
          </div>
      </div>

      {/* DataGrid */}
      <Box sx={{ height: 'auto', width: '100%' }}>
          <DataGrid rows={allData.map((row, i) => ({...row,srNo: i + 1 ,id: row.row_temp_id || `${pageIndex}-${i}`}))}
              columns={[
              { field: "qrcode_number",   headerName: "QR",                   flex: 1 },
              { field: "amount",          headerName: "Amount",               flex: 0.6 },
              { field: "cust_name",       headerName: "Customer Name/Mobile", flex: 1.2,
                  renderCell: (params) => 
                  {
                      const { cust_name, cust_mobile } = params.row;
                      return <span>{cust_name} / {cust_mobile} </span>;
                  }
              },
              { field: "cust_address",    headerName: "Address",          flex: 1.2 },
              { field: "gen_lot_number",  headerName: "Lot Number",       flex: 1 },
              { field: "loading_number",  headerName: "Loading Number",   flex: 1 },
              { field: "redeem_date",     headerName: "Date",             flex: 1 },
              { field: "action",          headerName: "Action",           flex: 0.7,
                  renderCell: params => (
                    <Button size="small" variant="contained" 
                      onClick={() =>navigate("redeemedcusthistory", { state: { lotCustName: params.row.cust_name,lotCustId: params.row.customer_id}})}
                    >History</Button>
                  )
              }
            ]}

            localeText={{ noRowsLabel: "No records found", noResultsOverlayLabel: "No matching data found", }}
            pageSize={Number(filters.pageSize)}
              onPageSizeChange={(newSize) => setFilters((prev) => ({ ...prev, pageSize: String(newSize) })) }
              pageSizeOptions={[10, 15, 20, 25, 40, 50, 80, 100]}
              pagination
              disableSelectionOnClick
              initialState={{ pagination: {paginationModel: {pageSize: Number(filters.pageSize),page: 0,},},
                  columns: {columnVisibilityModel: {  }}
              }}
              slots={{toolbar: CustomToolbar, footer: () => ( <CustomFooterWithLoadMore onLoadMore={() => fetchData(pageIndex + 1)} hasMoreData={hasMoreData} loading={loading} /> ), }}
              slotProps={{toolbar: {showQuickFilter: true,quickFilterProps: { debounceMs: 300 },},}}
              sx={{ '& .MuiDataGrid-cell': {fontSize: '13px', }, 
                    '& .MuiDataGrid-columnHeaderTitle': {fontSize: '15px',fontWeight: 'bold', whiteSpace: 'normal !important', 
                          lineHeight: '1.5', textAlign: 'center',},
                    "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {outline: "none"} }}
              rowHeight={38} headerHeight={45}
        />
      </Box>

      {loading && <GradientLoader text="Loading" />}
    </Container>
  );
};

export default LotRedemmCustList;
